import {
	Button,
	Dialog,
	DialogContent,
	DialogTitle,
	ModalOverlay,
	PaginationPageDefault,
	Select,
	Table,
	ToastCustom,
} from "@components";
import { FileUpload } from "@components/file-upload";
import { zodResolver } from "@hookform/resolvers/zod";
import { dayjs } from "@lib";
import { Clock, Download01 } from "@untitledui/icons";
import { type Key, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import {
	EXPENSE_IMPORT_ALLOWED_EXTENSIONS,
	EXPENSE_IMPORT_FIELD_CONFIG_BY_TYPE,
	EXPENSE_IMPORT_MAPPING_NONE,
	EXPENSE_IMPORT_PREVIEW_ROW_KEY_BY_FIELD,
	EXPENSE_IMPORT_SYSTEM_FIELD_LABELS,
	EXPENSE_IMPORT_TYPE_LABELS,
	REPORT_SUMMARY_CATEGORY_NAMES,
} from "../constants";
import {
	useImportExpensesConfirm,
	useImportExpensesHeader,
	useImportExpensesPreview,
} from "../hooks";
import { type ExpensesImportSchema, expensesImportSchema } from "../schemas";
import type {
	ExpenseImportFieldConfig,
	ExpenseImportHeaderResponse,
	ExpenseImportInvalidRow,
	ExpenseImportPreviewResponse,
	ExpenseImportSystemField,
	ExpenseImportTypeCode,
	ExpenseImportValidPreviewRow,
} from "../types";
import { downloadExpenseImportTemplate, parseSpreadsheetFile } from "../utils";

type ExpensesImportModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

type ImportStep = 1 | 2 | 3 | 4;

const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;

function formatListWithAnd(items: string[]): string {
	if (items.length === 0) {
		return "";
	}
	if (items.length === 1) {
		return items[0] ?? "";
	}
	if (items.length === 2) {
		return `${items[0]} e ${items[1]}`;
	}
	return `${items.slice(0, -1).join(", ")} e ${items.at(-1)}`;
}

function buildExtensionsAccept(): string {
	return EXPENSE_IMPORT_ALLOWED_EXTENSIONS.join(",");
}

function buildMappingFormData(
	type: ExpenseImportTypeCode,
	mapping: Record<string, string | null | undefined>,
	file: File
): FormData {
	const formData = new FormData();
	formData.append("Type", String(type));

	const configs = EXPENSE_IMPORT_FIELD_CONFIG_BY_TYPE[type];
	for (const config of configs) {
		const value = mapping[config.field];
		if (
			!value ||
			value === EXPENSE_IMPORT_MAPPING_NONE ||
			value.trim() === ""
		) {
			continue;
		}
		formData.append(`Mapping.Fields[${config.field}]`, value);
	}

	formData.append("File", file);

	return formData;
}

const formatSampleDateValue = (value: string): string => {
	const trimmedValue = value?.trim();
	if (!trimmedValue) {
		return value ?? "";
	}
	const parsedDate = dayjs(
		trimmedValue,
		[
			"DD/MM/YYYY",
			"D/M/YYYY",
			"YYYY-MM-DD",
			"YYYY-MM-DD HH:mm:ss",
			"DD-MM-YYYY",
			"M/DD/YY",
			"MM/DD/YY",
			"MM/DD/YYYY",
		],
		true
	);
	if (parsedDate.isValid()) {
		return parsedDate.format("DD/MM/YYYY");
	}
	return value;
};

const defaultValues: ExpensesImportSchema = {
	type: undefined,
	documentType: "spreadsheet",
	file: null,
	mapping: {},
};

export function ExpensesImportModal({
	isOpen,
	onClose,
}: ExpensesImportModalProps) {
	const [step, setStep] = useState<ImportStep>(1);
	const [headerResponse, setHeaderResponse] =
		useState<ExpenseImportHeaderResponse | null>(null);
	const [previewResponse, setPreviewResponse] =
		useState<ExpenseImportPreviewResponse | null>(null);
	const [invalidRowsPage, setInvalidRowsPage] = useState(1);

	const {
		control,
		watch,
		setValue,
		getValues,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<ExpensesImportSchema>({
		defaultValues,
		resolver: zodResolver(expensesImportSchema),
	});

	const type = watch("type");
	const typeCode: ExpenseImportTypeCode | null =
		type !== undefined && type !== null
			? (type as ExpenseImportTypeCode)
			: null;
	const documentType = watch("documentType");
	const file = watch("file");
	const mapping = watch("mapping") ?? {};

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		reset(defaultValues);
		setStep(1);
		setHeaderResponse(null);
		setPreviewResponse(null);
	}, [isOpen, reset]);

	// Reset mapping and parsed data when expense type changes
	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally only when type changes
	useEffect(() => {
		setValue("mapping", {});
		setHeaderResponse(null);
		setPreviewResponse(null);
	}, [type]);

	const importHeaderMutation = useImportExpensesHeader();
	const importPreviewMutation = useImportExpensesPreview();
	const importConfirmMutation = useImportExpensesConfirm();

	const handleClose = () => {
		reset(defaultValues);
		setStep(1);
		setHeaderResponse(null);
		setPreviewResponse(null);
		onClose();
	};

	const handleSelectTypeContinue = () => {
		setStep(2);
	};

	const handleUploadContinue = async (data: ExpensesImportSchema) => {
		const formFile = data.file;
		const formType = data.type;

		if (!(formFile && formType !== undefined && formType !== null)) {
			return;
		}

		const isAllowedExtension = EXPENSE_IMPORT_ALLOWED_EXTENSIONS.some((ext) =>
			formFile.name.toLowerCase().endsWith(ext)
		);

		if (!isAllowedExtension) {
			toast(
				ToastCustom({
					description:
						"Formato de arquivo não suportado. Envie um arquivo CSV, XLSX ou XLS.",
					title: "Formato inválido",
					variant: "error",
				})
			);
			return;
		}

		try {
			const result = await parseSpreadsheetFile(formFile);
			setHeaderResponse(result);
			setStep(3);
		} catch {
			try {
				const result = await importHeaderMutation.mutateAsync({
					type: formType,
					file: formFile,
				});
				setHeaderResponse(result);
				setStep(3);
			} catch {
				// Erro já tratado no hook useImportExpensesHeader
			}
		}
	};

	const handleMappingChange = (
		field: ExpenseImportSystemField,
		value: string | null
	) => {
		setValue("mapping", {
			...mapping,
			[field]: value === "" ? null : value,
		});
	};

	function handleMappingSelectValue(
		value: string | null,
		config: { field: ExpenseImportSystemField; isRequired: boolean }
	) {
		if (!value) {
			handleMappingChange(config.field, null);
			return;
		}
		if (value === EXPENSE_IMPORT_MAPPING_NONE) {
			if (config.isRequired) {
				toast(
					ToastCustom({
						description:
							"Campos obrigatórios não podem ser deixados sem mapeamento.",
						title: "Campo obrigatório",
						variant: "error",
					})
				);
				return;
			}
			handleMappingChange(config.field, EXPENSE_IMPORT_MAPPING_NONE);
			return;
		}

		const nextMapping = { ...mapping };
		for (const [field, mappedHeader] of Object.entries(nextMapping)) {
			if (field === config.field) {
				continue;
			}
			if (mappedHeader === value) {
				nextMapping[field] = null;
			}
		}
		nextMapping[config.field] = value;
		setValue("mapping", nextMapping);
	}

	const handleMappingContinue = async () => {
		const formType = getValues("type");
		const formFile = getValues("file");
		const formMapping = getValues("mapping") ?? {};

		if (
			!(
				formFile &&
				formType !== undefined &&
				formType !== null &&
				headerResponse
			)
		) {
			return;
		}

		const configs =
			EXPENSE_IMPORT_FIELD_CONFIG_BY_TYPE[formType as ExpenseImportTypeCode];
		const unmappedFields = configs.filter(
			(config: ExpenseImportFieldConfig) =>
				!formMapping[config.field] || formMapping[config.field]?.trim() === ""
		);

		if (unmappedFields.length > 0) {
			const fields = formatListWithAnd(
				unmappedFields.map((item: ExpenseImportFieldConfig) => item.label)
			);
			toast(
				ToastCustom({
					description: `Selecione a coluna ou "--Não incluir--" para: ${fields}.`,
					title: "Campos sem mapeamento",
					variant: "error",
				})
			);
			return;
		}

		const missingRequired = configs.filter(
			(config: ExpenseImportFieldConfig) =>
				config.isRequired &&
				formMapping[config.field] === EXPENSE_IMPORT_MAPPING_NONE
		);

		if (missingRequired.length > 0) {
			const fields = formatListWithAnd(
				missingRequired.map((item: ExpenseImportFieldConfig) => item.label)
			);
			toast(
				ToastCustom({
					description: `Mapeie todos os campos obrigatórios: ${fields}.`,
					title: "Campos obrigatórios não mapeados",
					variant: "error",
				})
			);
			return;
		}

		const formData = buildMappingFormData(
			formType as ExpenseImportTypeCode,
			formMapping,
			formFile
		);
		const result = await importPreviewMutation.mutateAsync(formData);
		setPreviewResponse(result);
		setInvalidRowsPage(1);
		setStep(4);
	};

	const handleConfirmImport = async () => {
		const formType = getValues("type");
		const formFile = getValues("file");
		const formMapping = getValues("mapping") ?? {};

		if (!(formFile && formType !== undefined && formType !== null)) {
			return;
		}

		const formData = buildMappingFormData(
			formType as ExpenseImportTypeCode,
			formMapping,
			formFile
		);
		await importConfirmMutation.mutateAsync(formData);
		handleClose();
	};

	const renderStep1 = () => (
		<div className="flex max-w-full flex-col overflow-hidden p-6 pt-4">
			<header className="space-y-2">
				<DialogTitle className="font-semibold text-lg text-primary">
					Importar despesas
				</DialogTitle>
				<p className="text-quaternary text-sm">
					Selecione o tipo de despesa para ver o modelo de planilha
					correspondente.
				</p>
			</header>

			<div className="space-y-2">
				<p className="font-medium text-secondary text-sm">Tipo de despesa</p>
				<Controller
					control={control}
					name="type"
					render={({ field }) => {
						const items = Object.entries(REPORT_SUMMARY_CATEGORY_NAMES).map(
							([key, value]) => ({
								id: key,
								label: value,
							})
						);
						const onChange = (value: Key | null) => {
							if (!value) {
								field.onChange(undefined);
								return;
							}
							field.onChange(Number(value) as ExpenseImportTypeCode);
							handleSelectTypeContinue();
						};
						return (
							<Select
								isInvalid={!!errors.type}
								items={items}
								onChange={onChange}
								placeholder="Selecione o tipo..."
								value={field.value?.toString() ?? ""}
							>
								{(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
							</Select>
						);
					}}
				/>
				{errors.type?.message ? (
					<p className="text-error-primary text-sm">{errors.type.message}</p>
				) : null}
			</div>
		</div>
	);

	const renderStep2 = () => {
		const mockedTemplateRows = typeCode
			? EXPENSE_IMPORT_FIELD_CONFIG_BY_TYPE[typeCode]
			: [];

		return (
			<div className="flex max-w-full flex-col overflow-hidden p-6 pt-4">
				<header className="mb-5 space-y-2">
					<h2 className="font-semibold text-lg text-primary">
						{typeCode
							? `Importar ${EXPENSE_IMPORT_TYPE_LABELS[typeCode]}`
							: "Importar despesas"}
					</h2>
				</header>
				{documentType === "spreadsheet" && (
					<div className="space-y-4">
						<div className="space-y-2">
							<p className="font-semibold text-secondary text-sm uppercase">
								Modelo para{" "}
								{typeCode ? EXPENSE_IMPORT_TYPE_LABELS[typeCode] : "despesa"}
							</p>
							<div className="overflow-hidden rounded-lg border border-border-secondary border-solid">
								<Table aria-label="Modelo para importação de despesas">
									<Table.Header className="h-8">
										<Table.Row>
											{mockedTemplateRows.map((row, index) => (
												<Table.Head
													className="whitespace-nowrap p-2 font-bold text-secondary text-xs leading-4 last:border-b-0"
													isRowHeader={index === 0}
													key={row.label}
												>
													{row.label}
													{!!row.isRequired && (
														<span className="text-error-primary">*</span>
													)}
												</Table.Head>
											))}
										</Table.Row>
									</Table.Header>
									<Table.Body>
										<Table.Row className="h-8 last:border-b-0">
											{mockedTemplateRows.map((row) => (
												<Table.Cell
													className="whitespace-nowrap p-2 text-secondary text-xs leading-4"
													key={`${row.label}-${row.value}`}
												>
													{row.value}
												</Table.Cell>
											))}
										</Table.Row>
									</Table.Body>
								</Table>
							</div>
						</div>

						<FileUpload.Root className="w-full">
							<FileUpload.DropZone
								accept={buildExtensionsAccept()}
								allowsMultiple={false}
								hint={
									"Envie apenas arquivos CSV, XLSX, XLS.\n" +
									`Tamanho máximo: ${MAX_FILE_SIZE_BYTES / 1024 / 1024} MB`
								}
								maxSize={MAX_FILE_SIZE_BYTES}
								onDropFiles={(files) => {
									const selected = files.item(0);
									if (selected) {
										setValue("file", selected);
									}
								}}
								onDropUnacceptedFiles={() =>
									toast(
										ToastCustom({
											description:
												"Arquivo não aceito. Verifique o formato ou tamanho.",
											title: "Erro no upload",
											variant: "error",
										})
									)
								}
								onSizeLimitExceed={() =>
									toast(
										ToastCustom({
											description: `Tamanho máximo excedido. Envie um arquivo de até ${MAX_FILE_SIZE_BYTES / 1024 / 1024} MB.`,
											title: "Arquivo grande demais",
											variant: "error",
										})
									)
								}
							/>
							{!!file && (
								<FileUpload.List>
									<FileUpload.ListItemProgressBar
										key={file.name}
										name={file.name}
										onDelete={() => setValue("file", null)}
										progress={100}
										size={file.size}
									/>
								</FileUpload.List>
							)}
						</FileUpload.Root>

						<div className="flex justify-between gap-3">
							<Button
								aria-label="Baixar modelo de planilha"
								className="w-full text-primary"
								disabled={!typeCode}
								onClick={() => {
									const downloaded = downloadExpenseImportTemplate(typeCode);

									if (!downloaded) {
										toast(
											ToastCustom({
												description:
													"Selecione um tipo de despesa para baixar o modelo.",
												title: "Ação indisponível",
												variant: "warning",
											})
										);
									}
								}}
								onKeyDown={(e) => {
									if (
										e.key !== "Enter" &&
										e.key !== " " &&
										e.key !== "Spacebar"
									) {
										return;
									}
									e.preventDefault();

									const downloaded = downloadExpenseImportTemplate(typeCode);

									if (!downloaded) {
										toast(
											ToastCustom({
												description:
													"Selecione um tipo de despesa para baixar o modelo.",
												title: "Ação indisponível",
												variant: "warning",
											})
										);
									}
								}}
								tabIndex={0}
								variant="outline"
							>
								<Download01 className="h-4 w-4 text-primary" />
								Baixar modelo de planilha
							</Button>
						</div>
					</div>
				)}

				<div className="mt-4 flex justify-between gap-3">
					<Button
						className="flex-1"
						onClick={() => setStep(1)}
						tabIndex={0}
						variant="outline"
					>
						Voltar
					</Button>
					<Button
						className="flex-1"
						disabled={importHeaderMutation.isPending || !file?.name}
						onClick={() => {
							handleSubmit(handleUploadContinue)();
						}}
						tabIndex={0}
					>
						{importHeaderMutation.isPending ? "Lendo planilha..." : "Continuar"}
					</Button>
				</div>
			</div>
		);
	};

	const renderStep3 = () => {
		if (!(typeCode && headerResponse)) {
			return null;
		}

		const configs = EXPENSE_IMPORT_FIELD_CONFIG_BY_TYPE[typeCode];
		const sampleRows = headerResponse.rows.slice(0, 5);

		return (
			<div className="flex max-w-full flex-col overflow-hidden p-6 pt-4">
				<header className="mb-5 space-y-2">
					<h2 className="font-semibold text-lg text-primary">
						Importar {EXPENSE_IMPORT_TYPE_LABELS[typeCode]}
					</h2>
					<p className="text-quaternary text-sm">
						Associe cada campo do sistema a uma coluna da sua planilha. Campos
						opcionais podem ser ignorados.
					</p>
				</header>

				<div className="space-y-3">
					{configs.map((config: ExpenseImportFieldConfig) => {
						const currentValue = mapping[config.field] ?? "";

						return (
							<div
								className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]"
								key={config.field}
							>
								<div className="space-y-0.5">
									<p className="font-medium text-secondary text-sm">
										{config.label}{" "}
										{config.isRequired ? (
											<span className="rounded-full border border-[#FEE4E2] bg-[#FEF3F2] px-1.5 text-[#B42318] text-[10px]">
												(obrigatório)
											</span>
										) : (
											<span className="rounded-full border border-[#E5E7EB] bg-[#F3F4F6] px-1.5 text-[#6B7280] text-[10px]">
												(opcional)
											</span>
										)}
									</p>
									{!!config.helper && (
										<p className="text-quaternary text-xs">{config.helper}</p>
									)}
								</div>

								<Select
									isInvalid={!!errors.type}
									items={[
										{
											id: EXPENSE_IMPORT_MAPPING_NONE,
											label: "--Não incluir--",
										},
										...headerResponse.headers.map((header) => ({
											id: header,
											label: header,
										})),
									]}
									onChange={(value: Key | null) => {
										handleMappingSelectValue(
											value as ExpenseImportSystemField,
											config
										);
									}}
									placeholder="Selecione a coluna..."
									value={currentValue ?? ""}
								>
									{(item) => (
										<Select.Item id={item.id}>{item.label}</Select.Item>
									)}
								</Select>
							</div>
						);
					})}
				</div>

				<div className="mt-4 space-y-2">
					<p className="font-semibold text-quaternary text-sm">
						Amostra dos dados ({headerResponse.rows.length} de 5 linhas)
					</p>

					<div className="overflow-hidden rounded-lg border border-border-secondary border-solid">
						<Table>
							<Table.Header>
								<Table.Row className="h-8">
									{headerResponse.headers.map((header, index) => (
										<Table.Head
											className="whitespace-nowrap p-2 font-bold text-secondary text-xs leading-4"
											isRowHeader={index === 0}
											key={header}
										>
											{header}
										</Table.Head>
									))}
								</Table.Row>
							</Table.Header>
							<Table.Body>
								{sampleRows.map(
									(row: Record<string, string>, index: number) => (
										<Table.Row
											className="h-8 last:border-b-0"
											key={`${row?.Data}-${index}`}
										>
											{headerResponse.headers.map((header) => (
												<Table.Cell
													className="whitespace-nowrap p-2 text-secondary text-xs leading-4"
													key={header}
												>
													{formatSampleDateValue(row[header])}
												</Table.Cell>
											))}
										</Table.Row>
									)
								)}
							</Table.Body>
						</Table>
					</div>
				</div>

				<div className="mt-4 flex justify-between gap-3">
					<Button
						className="flex-1"
						onClick={() => setStep(2)}
						tabIndex={0}
						variant="outline"
					>
						Voltar
					</Button>
					<Button
						className="flex-1"
						disabled={importPreviewMutation.isPending}
						onClick={handleMappingContinue}
						tabIndex={0}
					>
						{importPreviewMutation.isPending ? "Validando..." : "Continuar"}
					</Button>
				</div>
			</div>
		);
	};

	const renderInvalidRowsSection = (invalidRows: ExpenseImportInvalidRow[]) => {
		if (invalidRows.length === 0) {
			return null;
		}

		const INVALID_ROWS_PAGE_SIZE = 5;
		const totalPages = Math.max(
			1,
			Math.ceil(invalidRows.length / INVALID_ROWS_PAGE_SIZE)
		);
		const currentPage = Math.min(Math.max(invalidRowsPage, 1), totalPages);
		const startIndex = (currentPage - 1) * INVALID_ROWS_PAGE_SIZE;
		const endIndex = startIndex + INVALID_ROWS_PAGE_SIZE;
		const paginatedInvalidRows = invalidRows.slice(startIndex, endIndex);

		return (
			<section className="mt-4 space-y-3">
				<div className="overflow-hidden rounded-lg border border-border-secondary border-solid">
					<Table>
						<Table.Header className="h-8">
							<Table.Row>
								<Table.Head
									className="whitespace-nowrap p-2 font-bold text-secondary text-xs leading-4"
									isRowHeader
								>
									Linha
								</Table.Head>
								<Table.Head
									className="whitespace-nowrap p-2 font-bold text-secondary text-xs leading-4"
									isRowHeader
								>
									Erros
								</Table.Head>
							</Table.Row>
						</Table.Header>
						<Table.Body>
							{paginatedInvalidRows.map((row) => (
								<Table.Row className="h-8" key={row.rowNumber}>
									<Table.Cell>{row.rowNumber}</Table.Cell>
									<Table.Cell className="whitespace-nowrap p-2 text-secondary text-xs leading-4">
										<ul className="list-disc space-y-1 pl-4 text-quaternary text-xs leading-4">
											{row.errors.map((error, index) => {
												const fieldLabel =
													EXPENSE_IMPORT_SYSTEM_FIELD_LABELS[error.field] ??
													error.field;
												return (
													<li key={`${row.rowNumber}-${index}`}>
														<strong>{fieldLabel}:</strong> {error.message}
													</li>
												);
											})}
										</ul>
									</Table.Cell>
								</Table.Row>
							))}
						</Table.Body>
					</Table>

					<PaginationPageDefault
						className="p-5"
						onPageChange={(page) => {
							setInvalidRowsPage(Math.max(1, page));
						}}
						page={currentPage}
						total={totalPages}
					/>
				</div>
			</section>
		);
	};

	const renderStep4 = () => {
		if (!(typeCode && previewResponse)) {
			return null;
		}

		const { summary, validPreview, invalidRows } = previewResponse;
		const previewColumns = EXPENSE_IMPORT_FIELD_CONFIG_BY_TYPE[typeCode];

		function getPreviewCellValue(
			row: ExpenseImportValidPreviewRow,
			field: ExpenseImportSystemField
		): string | number | null {
			const key = EXPENSE_IMPORT_PREVIEW_ROW_KEY_BY_FIELD[
				field
			] as keyof ExpenseImportValidPreviewRow;
			const value = row[key];
			if (value === null || value === undefined) {
				return null;
			}
			return value;
		}

		return (
			<div className="flex max-w-full flex-col overflow-hidden p-6 pt-4">
				<header className="mb-5 space-y-2">
					<h2 className="font-semibold text-lg text-primary">
						Importar {EXPENSE_IMPORT_TYPE_LABELS[typeCode]}
					</h2>
				</header>

				<div className="space-y-1 text-secondary text-sm">
					<p className="mb-4 flex items-center gap-2">
						<Clock className="mb-0.5 size-4 shrink-0 text-fg-brand-primary" />
						<strong>
							{summary.validRows} despesas prontas para importação.
						</strong>
					</p>
					<p className="mb-4 text-quaternary">
						Total de linhas lidas: <strong>{summary.totalRowsRead}</strong> ·
						Linhas inválidas: <strong>{summary.invalidRows}</strong> · Linhas
						ignoradas por limite:{" "}
						<strong>{summary.ignoredRowsOverLimit}</strong>
					</p>
				</div>

				<section className="space-y-2">
					<p className="mb-1 font-medium text-secondary text-sm">
						Linhas válidas para importação
					</p>
					<div className="overflow-hidden rounded-lg border border-border-secondary border-solid">
						<Table>
							<Table.Header className="h-8">
								<Table.Row>
									<Table.Head
										className="whitespace-nowrap p-2 font-bold text-secondary text-xs leading-4"
										isRowHeader
									>
										Linha
									</Table.Head>
									{previewColumns.map((col: ExpenseImportFieldConfig) => (
										<Table.Head
											className="whitespace-nowrap p-2 font-bold text-secondary text-xs leading-4"
											key={col.field}
										>
											{col.label}
										</Table.Head>
									))}
								</Table.Row>
							</Table.Header>
							<Table.Body>
								{validPreview.map((row) => (
									<Table.Row
										className="h-8 last:border-b-0"
										key={row.rowNumber}
									>
										<Table.Cell className="whitespace-nowrap p-2 text-secondary text-xs leading-4">
											{row.rowNumber}
										</Table.Cell>
										{previewColumns.map((col: ExpenseImportFieldConfig) => {
											const value = getPreviewCellValue(row, col.field);
											return (
												<Table.Cell
													className="whitespace-nowrap p-2 text-secondary text-xs leading-4"
													key={col.field}
												>
													{value === null || value === undefined
														? "-"
														: String(value)}
												</Table.Cell>
											);
										})}
									</Table.Row>
								))}
							</Table.Body>
						</Table>
					</div>
				</section>

				{renderInvalidRowsSection(invalidRows)}

				<div className="mt-4 flex justify-between gap-3">
					<Button
						className="flex-1"
						onClick={() => setStep(3)}
						tabIndex={0}
						variant="outline"
					>
						Voltar
					</Button>
					<Button
						className="flex-1"
						disabled={
							importConfirmMutation.isPending || summary.validRows === 0
						}
						onClick={handleConfirmImport}
						tabIndex={0}
					>
						{importConfirmMutation.isPending
							? "Importando..."
							: `Importar ${summary.validRows} despesas`}
					</Button>
				</div>
			</div>
		);
	};

	return (
		<Dialog
			modal={false}
			onOpenChange={(open) => {
				if (!open) {
					handleClose();
				}
			}}
			open={isOpen}
		>
			{!!isOpen && <ModalOverlay isOpen={isOpen} />}
			<DialogContent
				className="z-auto max-h-[calc(100dvh-4rem)] max-w-[600px] gap-0 overflow-y-scroll p-0 sm:max-h-screen sm:overflow-y-auto"
				onInteractOutside={(event) => {
					event.preventDefault();
				}}
				onPointerDownOutside={(event) => {
					event.preventDefault();
				}}
			>
				{step === 1 && renderStep1()}
				{step === 2 && renderStep2()}
				{step === 3 && renderStep3()}
				{step === 4 && renderStep4()}
			</DialogContent>
		</Dialog>
	);
}
